import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useStoreSelector } from 'v3/data/store';
import { setNavigationStep } from 'v3/data/store/contractCard/actions';
import { ContractNavigationEnum } from 'v3/data/store/contractCard/enum';
import { setContractStepperState } from 'v3/data/store/contractCard/stepper/actions';
import { ContractStepperEnum } from 'v3/data/store/contractCard/stepper/enum';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { setDevice } from 'v3/data/store/device/actions';
import { IDevice } from 'v3/data/store/device/types';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { getStorePermissions } from 'v3/data/store/stores/actions';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { updateNavigationStepByContractingState } from 'v3/pages/Contracting/Details/Cards/ContractInformation/Steps/state-navigation-mapping';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import * as api from 'v3/services/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { ChangePurchaseInfoModal } from 'v3/shared/components/Modal/ChangePurchaseInfoModal';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { VoucherStatusEnum } from 'v3/shared/enums/voucher-status.enum';
import { deviceFormSchema } from 'v3/shared/schemas/device';
import { AlignButtons, ContentStyled } from './styles';

interface IFlowStep {
  isFlowDatasys: boolean;
}

export const PurchaseInformation = ({ isFlowDatasys }: IFlowStep) => {
  const { isMobile } = useBreakpoints();
  const { contractingState } = useStoreSelector((store) => store.contracting);
  const { currentStep } = useStoreSelector((store) => store.navigate);
  const { device } = useStoreSelector((store) => store);
  const hasDevice = Boolean(device.imei);
  const isContractingFinished = contractingState === ContractingStateEnum.ContractingFinished;
  const shouldDisableAll = contractingState !== ContractingStateEnum.WaitingContractEffectiveness;
  const [isEditing, setIsEditing] = useState(false);
  const [openPurchaseInfoEditModal, setOpenPurchaseInfoEditModal] = useState(false);
  const { isMaster } = getStorePermissions();
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;
  const [isDatasysDevice, setDatasysDevice] = useState(false);
  const [removeEanFeatureEnabled, setRemoveEanFeatureEnabled] = useState(false);

  const formSchema = useMemo(() => deviceFormSchema(!removeEanFeatureEnabled), [removeEanFeatureEnabled]);
  const {
    control,
    getValues,
    setValue,
    formState: { isValid },
  } = useForm<IDevice>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    values: device,
    resolver: yupResolver(formSchema) as any,
  });

  const handleOpenChangePurchaseInfo = () => {
    setOpenPurchaseInfoEditModal(true);
  };

  const fetchRemoveEanFeatureFlag = useCallback(async () => {
    const response = await api.getFlagStatus(FeatureFlagEnum.IKCLOUD_50567_REMOVE_EAN);
    if (response.data.enabled) {
      setRemoveEanFeatureEnabled(true);
    }
  }, []);

  useEffect(() => {
    fetchRemoveEanFeatureFlag();
  }, []);

  const cleanPurchasePayload = (deviceData: IDevice) => {
    const payload = {
      ...deviceData,
      ean: deviceData.ean || undefined,
    };
    return payload;
  };

  const handleChangePurchaseInfo = async (data: IDevice) => {
    try {
      const payload = cleanPurchasePayload(data);
      const response = await api.updatePurchaseInfo(payload, contractingKey);
      showSnackbar('Informações da compra editadas com sucesso', 'success', true);
      setDevice(response.data);
      setOpenPurchaseInfoEditModal(false);
    } catch (error) {
      showSnackbar('Erro ao editar informações da compra', 'error', true);
      console.error(error);
    }
  };
  const handleDatasysMaster = useCallback(async () => {
    try {
      const response = await api.getVoucher(contractingKey);
      const shouldEditDevice = response.data?.status === VoucherStatusEnum.Applied && isMaster;
      setDatasysDevice(shouldEditDevice);
    } catch (error) {
      setDatasysDevice(false);
    }
  }, [contractingKey, isMaster]);

  useEffect(() => {
    handleDatasysMaster();
  }, [handleDatasysMaster]);

  const handleSaveClick = async () => {
    try {
      const deviceData = getValues();
      const payload = cleanPurchasePayload(deviceData);
      const response = await api.upsertDevice(contractingKey, payload);
      setDevice(response.data);

      setIsEditing(false);
      setContractStepperState({
        stepName: ContractStepperEnum.DeviceDetails,
        error: false,
        isCompleted: true,
      });
      updateNavigationStepByContractingState(contractingState, Boolean(device.imei), false, true);
    } catch (error) {
      showErrorDialog('Erro ao salvar dados do device', error);
    }
  };

  useEffect(() => {
    if (
      currentStep === ContractNavigationEnum.DEVICE_DETAILS &&
      contractingState === ContractingStateEnum.WaitingContractEffectiveness &&
      device.imei
    ) {
      if (isEditing) {
        setContractStepperState({
          stepName: ContractStepperEnum.DeviceDetails,
          error: false,
          isCompleted: false,
        });
      } else {
        updateNavigationStepByContractingState(contractingState, true);
      }
    }
  }, [currentStep, contractingState, device, isEditing]);

  const handleEditClick = () => {
    setIsEditing(true);
    setNavigationStep(ContractNavigationEnum.DEVICE_DETAILS);
    setContractStepperState({
      stepName: ContractStepperEnum.DeviceDetails,
      error: false,
      isCompleted: false,
    });
  };

  useEffect(() => {
    if (hasDevice) {
      setValue('invoiceNumber', device.invoiceNumber);
      setValue('invoiceSeries', device.invoiceSeries);
      setValue('imei', device.imei);
      setValue('ean', device.ean);
    }
  }, [currentStep, device, setValue]);

  const shouldDisableDeviceInputs = (!isEditing && hasDevice) || shouldDisableAll || isFlowDatasys;

  return (
    <ContentStyled>
      <Box sx={{ opacity: shouldDisableAll ? '0.8' : '1' }}>
        <Grid container spacing={2} justifyContent="center">
          <Grid size={{ xs: 12, sm: 12, lg: 4, md: 6 }}>
            <InputFormController
              data-testid="input-invoice-number"
              inputType="text"
              formControl={control}
              formControlName="invoiceNumber"
              label="Número da nota fiscal"
              fullWidth
              disabled={shouldDisableDeviceInputs}
              showCheckbox={false}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, lg: 4, md: 6 }}>
            <InputFormController
              data-testid="input-invoice-series"
              inputType="text"
              formControl={control}
              formControlName="invoiceSeries"
              label="Série nota fiscal"
              fullWidth
              disabled={shouldDisableDeviceInputs}
              showCheckbox={false}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, lg: 4, md: 6 }}>
            <InputFormController
              data-testid="input-imei"
              inputType="text"
              formControl={control}
              formControlName="imei"
              label="IMEI"
              fullWidth
              disabled={shouldDisableDeviceInputs}
              showCheckbox={false}
            />
          </Grid>
          {!removeEanFeatureEnabled && (
            <Grid size={{ xs: 12, sm: 12, lg: 4, md: 6 }}>
              <InputFormController
                data-testid="input-ean"
                inputType="text"
                formControl={control}
                formControlName="ean"
                label="EAN"
                fullWidth
                disabled={shouldDisableDeviceInputs}
                showCheckbox={false}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      {!isDatasysDevice && isMaster && isContractingFinished && (
        <AlignButtons>
          <FlexButton
            text={isMobile ? 'Editar informações' : 'Editar informações da compra'}
            handleClick={handleOpenChangePurchaseInfo}
            fullWidth={isMobile}
            size="small"
          />
        </AlignButtons>
      )}

      {!isContractingFinished && (
        <AlignButtons>
          {!hasDevice || isEditing ? (
            <FlexButton
              text="Salvar informações"
              handleClick={handleSaveClick}
              disabled={!isValid || shouldDisableAll}
              fullWidth={isMobile}
              size="small"
            />
          ) : (
            <TextButton
              text="Editar informações"
              handleClick={handleEditClick}
              fullWidth={isMobile}
              size="small"
              $outline
            />
          )}
        </AlignButtons>
      )}
      <ChangePurchaseInfoModal
        title="Editar informações da compra"
        active={openPurchaseInfoEditModal}
        onClose={() => setOpenPurchaseInfoEditModal(false)}
        onSave={handleChangePurchaseInfo}
        currentDevice={device}
        removeEanFeatureEnabled={removeEanFeatureEnabled}
      />
    </ContentStyled>
  );
};
